import { Box } from "@repo/components/src/ui/box";
import { Card } from "@repo/components/src/ui/card";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { formatMoneyFromFragment } from "@repo/components/src/utils/money";
import {
  formatNumber,
  formatPercentage,
  formatSeparators,
} from "@repo/components/src/utils/numbers";
import {
  CampaignAnalyticFragmentFragment,
  CampaignProjectionFragmentFragment,
} from "@repo/graphql/documents/campaign/fragment.generated";
import React, { useState } from "react";
import Section from "../shared/section";

type PlatformKey = "all" | "instagram" | "tiktok";

type Props = {
  report: CampaignAnalyticFragmentFragment;
};

/** A metric benchmarked against what these creators habitually achieve. */
type MetricCard = {
  label: string;
  actual: number;
  low: number;
  typical: number;
  high: number;
  actualText: string;
  lowText: string;
  typicalText: string;
  highText: string;
  lowerIsBetter?: boolean;
};

const ProjectionSection = React.memo(({ report }: Props) => {
  const all = report.projection;
  const ig = report.projectionInstagram;
  const tt = report.projectionTiktok;

  const [platform, setPlatform] = useState<PlatformKey>("all");

  if (!all || !all.hasData) return null;

  const active =
    platform === "instagram" ? ig : platform === "tiktok" ? tt : all;

  return (
    <Section
      title="Rendimiento vs. historial del creador"
      description="Cada métrica se compara con lo que estos mismos creadores suelen lograr en sus publicaciones recientes del mismo tipo."
      right={
        <PlatformTabs
          value={platform}
          onChange={setPlatform}
          hasIg={!!ig?.hasData}
          hasTt={!!tt?.hasData}
        />
      }
    >
      {!active || !active.hasData ? (
        <Text size="sm" className="text-typography-500">
          Sin contenido en esta plataforma para comparar.
        </Text>
      ) : (
        <Grid projection={active} />
      )}
    </Section>
  );
});

ProjectionSection.displayName = "ProjectionSection";

const PlatformTabs = ({
  value,
  onChange,
  hasIg,
  hasTt,
}: {
  value: PlatformKey;
  onChange: (v: PlatformKey) => void;
  hasIg: boolean;
  hasTt: boolean;
}) => {
  const tabs: { key: PlatformKey; label: string; disabled: boolean }[] = [
    { key: "all", label: "Todos", disabled: false },
    { key: "instagram", label: "Instagram", disabled: !hasIg },
    { key: "tiktok", label: "TikTok", disabled: !hasTt },
  ];
  return (
    <HStack space="xs" className="bg-background-100 p-1 rounded-lg">
      {tabs.map((tab) => {
        const active = value === tab.key;
        return (
          <Pressable
            key={tab.key}
            onPress={() => !tab.disabled && onChange(tab.key)}
            disabled={tab.disabled}
            className={
              "px-3 py-1.5 rounded-md " +
              (active && !tab.disabled ? "bg-background-0" : "bg-transparent")
            }
          >
            <Text
              size="xs"
              className={
                "text-center font-semibold " +
                (active && !tab.disabled
                  ? "text-typography-900"
                  : tab.disabled
                    ? "text-typography-300"
                    : "text-typography-500")
              }
            >
              {tab.label}
            </Text>
          </Pressable>
        );
      })}
    </HStack>
  );
};

const Grid = ({
  projection,
}: {
  projection: CampaignProjectionFragmentFragment;
}) => {
  const { reach, views, engagementRate, cpm } = projection;

  const cards: MetricCard[] = [
    {
      label: "Alcance",
      actual: reach.actual,
      low: reach.expectedLow,
      typical: reach.expectedTypical,
      high: reach.expectedHigh,
      actualText: formatSeparators(reach.actual),
      lowText: formatSeparators(reach.expectedLow),
      typicalText: formatSeparators(reach.expectedTypical),
      highText: formatSeparators(reach.expectedHigh),
    },
    {
      label: "Reproducciones",
      actual: views.actual,
      low: views.expectedLow,
      typical: views.expectedTypical,
      high: views.expectedHigh,
      actualText: formatSeparators(views.actual),
      lowText: formatSeparators(views.expectedLow),
      typicalText: formatSeparators(views.expectedTypical),
      highText: formatSeparators(views.expectedHigh),
    },
    {
      label: "Engagement rate",
      actual: engagementRate.actual,
      low: engagementRate.expectedLow,
      typical: engagementRate.expectedTypical,
      high: engagementRate.expectedHigh,
      actualText: formatPercentage(engagementRate.actual),
      lowText: formatPercentage(engagementRate.expectedLow),
      typicalText: formatPercentage(engagementRate.expectedTypical),
      highText: formatPercentage(engagementRate.expectedHigh),
    },
  ];

  if (
    cpm.actual &&
    cpm.expectedLow &&
    cpm.expectedTypical &&
    cpm.expectedHigh
  ) {
    cards.push({
      label: "CPM efectivo",
      actual: cpm.actual.amount,
      low: cpm.expectedLow.amount,
      typical: cpm.expectedTypical.amount,
      high: cpm.expectedHigh.amount,
      actualText: formatMoneyFromFragment(cpm.actual),
      lowText: formatMoneyFromFragment(cpm.expectedLow),
      typicalText: formatMoneyFromFragment(cpm.expectedTypical),
      highText: formatMoneyFromFragment(cpm.expectedHigh),
      lowerIsBetter: true,
    });
  }

  return (
    <VStack space="md">
      <HStack space="md" className="flex-wrap items-stretch">
        {cards.map((card) => (
          <BaselineCard key={card.label} {...card} />
        ))}
      </HStack>
      <Basis sampleSize={reach.sampleSize} basis={reach.basis} />
    </VStack>
  );
};

/** Explains where the expected band came from, so nobody reads it as a promise. */
const Basis = ({
  sampleSize,
  basis,
}: {
  sampleSize: number;
  basis: string;
}) => {
  if (basis === "followers" || sampleSize === 0) {
    return (
      <Text size="2xs" className="text-typography-400">
        Ningún creador tenía suficientes publicaciones previas, así que el rango
        se estimó a partir de su número de seguidores. Tómalo como referencia
        gruesa.
      </Text>
    );
  }
  const history = (
    <>
      Rango habitual calculado sobre {formatNumber(sampleSize, 0)} publicaciones
      previas de los mismos creadores (mismo tipo de pieza, últimos 180 días).
      Es su desempeño típico, no una meta comprometida.
    </>
  );
  return (
    <Text size="2xs" className="text-typography-400">
      {history}
      {basis === "mixed"
        ? " Algunos creadores no tenían historial suficiente; su tramo del rango se estimó a partir de sus seguidores."
        : null}
    </Text>
  );
};

/**
 * Ratio of the result to what these creators normally achieve. `null` when
 * there is no usable baseline, in which case no verdict is claimed.
 */
const performanceIndex = (actual: number, typical: number): number | null =>
  typical > 0 ? actual / typical : null;

const BaselineCard = ({
  label,
  actual,
  low,
  typical,
  high,
  actualText,
  lowText,
  typicalText,
  highText,
  lowerIsBetter = false,
}: MetricCard) => {
  const index = performanceIndex(actual, typical);
  const outperformed =
    index === null ? null : lowerIsBetter ? index < 1 : index > 1;

  return (
    <Card
      variant="outline"
      className="rounded-xl"
      size="md"
      style={{ flex: 1, minWidth: 240 } as object}
    >
      <VStack space="sm">
        <Text
          size="2xs"
          className="text-typography-500 uppercase font-bold tracking-wide"
        >
          {label}
        </Text>
        <HStack space="sm" className="items-baseline flex-wrap">
          <Heading size="2xl" className="text-typography-900">
            {actualText}
          </Heading>
          {index !== null ? (
            <IndexPill index={index} outperformed={!!outperformed} />
          ) : null}
        </HStack>

        {typical > 0 ? (
          <>
            <BaselineBar
              actual={actual}
              low={low}
              typical={typical}
              high={high}
              outperformed={!!outperformed}
            />
            <Text size="xs" className="text-typography-500">
              Habitual{" "}
              <Text size="xs" className="text-typography-900 font-semibold">
                {typicalText}
              </Text>
              {"  ·  "}
              {lowText} – {highText}
            </Text>
          </>
        ) : (
          <Text size="xs" className="text-typography-400">
            Sin historial suficiente para comparar.
          </Text>
        )}
      </VStack>
    </Card>
  );
};

const IndexPill = ({
  index,
  outperformed,
}: {
  index: number;
  outperformed: boolean;
}) => (
  <Box
    className={
      "px-2 py-0.5 rounded-full " +
      (outperformed ? "bg-success-100" : "bg-background-100")
    }
  >
    <Text
      size="2xs"
      className={
        "font-bold " +
        (outperformed ? "text-success-700" : "text-typography-600")
      }
    >
      {formatNumber(index, 1)}× lo habitual
    </Text>
  </Box>
);

/**
 * Bar spanning the creators' typical range, with a tick at their median and a
 * marker where the campaign actually landed.
 */
const BaselineBar = ({
  actual,
  low,
  typical,
  high,
  outperformed,
}: {
  actual: number;
  low: number;
  typical: number;
  high: number;
  outperformed: boolean;
}) => {
  // Pad the scale so a marker sitting at an extreme still has breathing room.
  // The floor scales with the data: an absolute epsilon vanishes into rounding
  // at real magnitudes, collapsing a flat band onto the left edge.
  const minVal = Math.min(actual, low);
  const maxVal = Math.max(actual, high);
  const padding = Math.max(
    (maxVal - minVal) * 0.08,
    Math.abs(typical) * 1e-3,
    Number.MIN_VALUE,
  );
  const scaleMin = minVal - padding;
  const span = maxVal + padding - scaleMin;
  const pct = (value: number) =>
    span > 0 ? ((value - scaleMin) / span) * 100 : 50;

  const lowPct = pct(low);
  const bandWidth = Math.max(pct(high) - lowPct, 2);

  return (
    <Box className="relative h-2 rounded-full bg-background-100">
      <Box
        className="absolute top-0 h-2 rounded-full bg-background-200"
        style={{ left: `${lowPct}%`, width: `${bandWidth}%` }}
      />
      <Box
        className="absolute -top-0.5 w-0.5 h-3 rounded-full bg-typography-400"
        style={{ left: `${pct(typical)}%` }}
      />
      <Box
        className={
          "absolute -top-1 w-1 h-4 rounded-full " +
          (outperformed ? "bg-success-600" : "bg-brand-500")
        }
        style={{ left: `${pct(actual)}%` }}
      />
    </Box>
  );
};

export default ProjectionSection;
